import * as React from 'react'
import { ComponentProps } from 'react'
import { Legend } from 'recharts'
import { Payload as LegendPayload } from 'recharts/types/component/DefaultLegendContent'
import { useTranslation } from 'react-i18next'

import { MultiChart } from '@organisms'
import { UsageCostResponseBody, UsageCostLabelsMap } from '@/hooks'
import { processChartData } from '@/utils/processChartData'
import { shortenNumber } from '@/utils/shortenNumber'
import { formatNumber, formatValueWithUnit } from '@/utils/formatValueWithUnit'
import { formatDateForChart } from '@/utils/formatDateForChart'
import {
    doesDataHasVariance,
    getVarianceDataSetPreviousLabel,
} from '@/utils/variance'
import { useAnalyticsFiltersStore } from '@/features/Analytics/stores/useAnalyticsFiltersStore/useAnalyticsFiltersStore'
import useAnalyticsStore from '@/features/Analytics/stores/useAnalyticsStore/useAnalyticsStore'
import { VarianceBadge } from '@/components'
import { DataSummary } from '@/components/common/LineChartLegends/DataSummary'
import { DefaultLegend } from '@/components/common/LineChartLegends/DefaultLegend'
import { useChartColorStore } from '@/stores/useChartColorStore/useChartColorStore'
import { UsageAndCostSummaryDropdownContent } from './UsageAndCostSummaryDropdownContent'
import { useUsageAndCostDataCompleteness } from '../../hooks/useUsageAndCostDataCompleteness'
import { UsageAndCostTooltipWrapper } from './UsageAndCostTooltipWrapper'

type UsageAndCostChartProps = {
    data: UsageCostResponseBody
    dataSets?: Array<string>
    showSummaryDropdown?: boolean
    labelsMap: UsageCostLabelsMap
    minHeight?: boolean
    totalUsns: number
}

export const UsageAndCostChart: React.FC<UsageAndCostChartProps> = (props) => {
    const {
        data,
        labelsMap,
        dataSets = [],
        showSummaryDropdown,
        minHeight,
        totalUsns,
    } = props
    const { t } = useTranslation('translations')
    const processedData = React.useMemo(
        () =>
            processChartData(data.items, labelsMap, 'date', {
                include: dataSets,
            }),
        [data, dataSets, labelsMap]
    )

    const updateColors = useChartColorStore((state) => state.updateColors)
    const setIsChartSummaryHovered = useAnalyticsStore(
        (state) => state.usageAndCost.setIsChartSummaryHovered
    )

    React.useEffect(() => {
        setIsChartSummaryHovered(false)
    }, [setIsChartSummaryHovered])

    // Get dataset color indices with index reuse
    const datasetIndices = React.useMemo(() => {
        return updateColors(dataSets)
    }, [dataSets, updateColors])

    // Create datasetColorIndexMap based on dataset indices
    const datasetColorIndexMap = React.useMemo(() => {
        const result: Record<string, number> = {}

        dataSets
            .filter((key) => key !== 'date')
            .forEach((datasetKey) => {
                const label = labelsMap[datasetKey as keyof typeof labelsMap]
                if (label) {
                    result[label] = datasetIndices[datasetKey]
                }
            })

        return result
    }, [dataSets, labelsMap, datasetIndices])

    const lineChartData = extractData(processedData, 'usage_', labelsMap)
    const areaChartData = extractData(processedData, 'cost_', labelsMap)
    const barChartData = extractData(processedData, 'demand_', labelsMap)

    const hasDataVariance = doesDataHasVariance(data.items)

    const dataCompletenessMap = useUsageAndCostDataCompleteness({
        data: data.items,
        totalUsns: totalUsns,
    })

    const tooltipWrapper = React.useMemo(() => {
        const TooltipWrapperComponent: React.FC<{
            label: string
            originalDateLabel?: string
            tooltipItemsWrapperClassname?: string
            children: React.ReactNode
        }> = (wrapperProps) => {
            const timestampForCompleteness =
                wrapperProps.originalDateLabel || wrapperProps.label

            const timestampNumber = Number(timestampForCompleteness)
            const dataCompleteness = !isNaN(timestampNumber)
                ? dataCompletenessMap.get(timestampNumber)
                : undefined

            return (
                <UsageAndCostTooltipWrapper
                    label={wrapperProps.label}
                    tooltipItemsWrapperClassname={
                        wrapperProps.tooltipItemsWrapperClassname
                    }
                    dataCompleteness={dataCompleteness}
                    hasVarianceBadge={hasDataVariance}
                >
                    {wrapperProps.children}
                </UsageAndCostTooltipWrapper>
            )
        }
        return TooltipWrapperComponent
    }, [dataCompletenessMap, hasDataVariance])

    // Detect when parent/grandparent should appear as lines
    const parentChildRelationships = React.useMemo(() => {
        const relationships = [
            {
                parent: 'cost_totalInvoiced',
                children: [
                    'cost_commodity_totalCommodityCost',
                    'cost_transport_totalTransportCost',
                    'cost_taxes',
                    'cost_other',
                ],
            },
            {
                parent: 'cost_commodity_totalCommodityCost',
                children: [
                    'cost_commodity_supply',
                    'cost_commodity_transmission',
                    'cost_commodity_capacity',
                ],
            },
            {
                parent: 'cost_transport_totalTransportCost',
                children: [
                    'cost_transport_tariff',
                    'cost_transport_4cp4ncp',
                    'cost_transport_billingDemand',
                ],
            },
        ]

        const parentsAsLines: string[] = []

        relationships.forEach(({ parent, children }) => {
            // Check if parent is selected AND all children are selected
            const parentSelected = dataSets.includes(parent)
            const allChildrenSelected = children.every((child) =>
                dataSets.includes(child)
            )

            if (parentSelected && allChildrenSelected) {
                parentsAsLines.push(parent)
            }
        })

        return { relationships, parentsAsLines }
    }, [dataSets])

    const dataWithParentLines = React.useMemo(() => {
        // Early return if no parent lines to process
        if (parentChildRelationships.parentsAsLines.length === 0) {
            return processedData
        }

        return processedData.map((item) => {
            const newItem = { ...item }

            parentChildRelationships.parentsAsLines.forEach((parentKey) => {
                const parentLabel =
                    labelsMap[parentKey as keyof typeof labelsMap]
                if (!parentLabel) return

                const parentValue = item[parentLabel] as number
                if (!parentValue) return

                const offset = parentValue * 0.007
                const lineKey = `${parentLabel}_line`

                let commodityValue: number
                let combinedValue: number

                switch (parentKey) {
                    case 'cost_commodity_totalCommodityCost':
                        // Line at top of commodity stack
                        newItem[lineKey] = parentValue + offset
                        break

                    case 'cost_transport_totalTransportCost':
                        // Line at top of combined commodity + transport stack
                        commodityValue =
                            (item[
                                labelsMap.cost_commodity_totalCommodityCost
                            ] as number) || 0
                        combinedValue = commodityValue + parentValue
                        newItem[lineKey] = combinedValue + combinedValue * 0.007
                        break

                    case 'cost_totalInvoiced':
                        // Line at very top of all stacks (grandparent)
                        newItem[lineKey] = parentValue + offset
                        break

                    default:
                        // Fallback for any other parent types
                        newItem[lineKey] = parentValue + offset
                        break
                }
            })

            return newItem
        })
    }, [processedData, parentChildRelationships.parentsAsLines, labelsMap])

    const renderTooltipItem: ComponentProps<
        typeof MultiChart
    >['renderTooltipItem'] = (entry) => {
        const payload = entry.payload as Record<string, unknown>
        let previous = 0
        let current = 0
        const label = getVarianceDataSetPreviousLabel({
            dataKey: entry.dataKey as string,
            labelsMap,
        })

        if (label) {
            current = entry.value as number
            previous = payload[label] as number
        }

        return (
            <>
                <div className="col-start-1 flex items-center gap-1">
                    <div
                        className="h-2 w-2 shrink-0 rounded-full bg-[currentColor]"
                        style={{
                            color: entry.color,
                        }}
                    />
                    <div className="text-label-xsmall whitespace-nowrap text-left">
                        {entry.name}
                    </div>
                </div>
                <span aria-hidden="true" />
                <div className="text-body-small col-start-3 justify-self-end text-right font-bold">
                    {formatNumber(entry.value as string | number)}
                </div>
                {hasDataVariance &&
                    (previous && current ? (
                        <VarianceBadge
                            previous={previous}
                            current={current}
                            isReversed={true}
                            classes="justify-self-end"
                        />
                    ) : (
                        <div />
                    ))}
            </>
        )
    }

    const renderLegend = React.useCallback(
        (props: { payload?: LegendPayload[] }) => {
            const payload = props.payload
                ? props.payload.filter((item, index, array) => {
                      // Filter out fake elements and line keys
                      if (item.dataKey === 'fakeTotalCommodityCost')
                          return false
                      if (
                          typeof item.dataKey === 'string' &&
                          item.dataKey.includes('_line')
                      )
                          return false

                      // Remove duplicates by keeping only first occurrence
                      const firstIndex = array.findIndex(
                          (p) => p.dataKey === item.dataKey
                      )
                      return firstIndex === index
                  })
                : undefined

            if (!payload) {
                return null
            }

            const reversedPayload = [...payload].reverse()

            return (
                <div className="flex items-start justify-between gap-4 pb-8">
                    <DefaultLegend payload={reversedPayload} />
                    <DataSummary
                        showSummaryDropdown={Boolean(showSummaryDropdown)}
                        label={t('analytics.summary_dropdown.label')}
                        disabled={payload.length === 0}
                        renderDropdownContent={({
                            onMouseEnter,
                            onMouseLeave,
                            onClose,
                        }) => (
                            <UsageAndCostSummaryDropdownContent
                                payload={payload}
                                hasDataVariance={hasDataVariance}
                                labelsMap={labelsMap}
                                data={processedData}
                                totalUsns={totalUsns}
                                dataCompletenessMap={dataCompletenessMap}
                                onMouseEnter={onMouseEnter}
                                onMouseLeave={onMouseLeave}
                                onClose={onClose}
                            />
                        )}
                        onDropdownVisibilityChange={setIsChartSummaryHovered}
                        onDropdownHoverChange={setIsChartSummaryHovered}
                    />
                </div>
            )
        },
        [
            hasDataVariance,
            labelsMap,
            processedData,
            showSummaryDropdown,
            setIsChartSummaryHovered,
            t,
            totalUsns,
            dataCompletenessMap,
        ]
    ) as ComponentProps<typeof Legend>['content']

    return (
        <MultiChart
            datasetColorIndexMap={datasetColorIndexMap}
            showTooltip={() => {
                return (
                    !useAnalyticsStore.getState().usageAndCost
                        .isChartSummaryHovered &&
                    !useAnalyticsFiltersStore.getState().areFiltersOpen
                )
            }}
            renderLegend={renderLegend}
            id="usage-and-cost-line-chart"
            data={dataWithParentLines}
            lineChartData={lineChartData}
            areaChartData={areaChartData}
            barChartData={barChartData}
            xKey="date"
            renderTooltipItem={renderTooltipItem}
            tooltipTitleFormatter={formatDateForChart}
            tooltipWrapper={tooltipWrapper}
            tooltipItemsWrapperClassname={
                hasDataVariance
                    ? 'grid grid-cols-[auto_auto_auto_auto] items-center overflow-hidden gap-x-2 gap-y-1 z-[50]'
                    : 'grid grid-cols-[auto_auto_auto] items-center overflow-hidden gap-x-2 gap-y-1 z-[50]'
            }
            lineYAxisTickFormatter={(number: number) => {
                const { value, sign } = shortenNumber(number)
                const label = formatValueWithUnit(
                    {
                        value,
                        sign,
                        unit: data.items[0]?.usage.unit ?? '',
                    },
                    {}
                )

                return label.replace(/ /g, '\u00A0')
            }}
            barYAxisTickFormatter={(number: number) => {
                const { value, sign } = shortenNumber(number)
                const label = formatValueWithUnit(
                    {
                        value,
                        sign,
                        unit: data.items[0]?.demand.unit ?? '',
                    },
                    {}
                )

                return label.replace(/ /g, '\u00A0')
            }}
            xAxisTickFormatter={formatDateForChart}
            areaYAxisTickFormatter={(number: number) => {
                const { value, sign } = shortenNumber(number)
                return formatValueWithUnit(
                    {
                        sign,
                        value,
                        unit: data.items[0]?.cost.unit ?? '',
                    },
                    {}
                )
            }}
            getAreaProps={(label) => {
                return getAreaProps(label, labelsMap, dataSets)
            }}
            minHeight={minHeight}
        />
    )
}

const getAreaProps = (
    label: string,
    labelsMap: UsageCostLabelsMap,
    dataSets: string[]
) => {
    const key =
        Object.keys(labelsMap).find(
            (key: string) => labelsMap[key as keyof typeof labelsMap] === label
        ) ?? label
    const splitKey = key.split('_')
    const childKey = splitKey.pop()
    let stackId = splitKey.join('_')

    if (childKey === 'totalInvoiced') {
        stackId = 'totalInvoiced'
    }
    if (childKey === 'totalCommodityCost') {
        stackId = 'cost'
    }
    if (childKey === 'totalTransportCost') {
        stackId = 'cost'
    }

    if (childKey === 'fakeTotalCommodityCost') {
        stackId = 'cost_transport'
    }

    // Check if individual children should stack together
    const isCommodityChild =
        childKey === 'supply' ||
        childKey === 'transmission' ||
        childKey === 'capacity'
    const isTransportChild =
        childKey === 'tariff' ||
        childKey === '4cp4ncp' ||
        childKey === 'billingDemand'

    if (isCommodityChild || isTransportChild) {
        // Check if their respective parents are selected
        const commodityParentSelected = dataSets.includes(
            'cost_commodity_totalCommodityCost'
        )
        const transportParentSelected = dataSets.includes(
            'cost_transport_totalTransportCost'
        )

        // If neither parent is selected, all children stack together
        if (!commodityParentSelected && !transportParentSelected) {
            stackId = 'cost'
        }
    }

    return {
        stackId: stackId,
    }
}

const extractData = (
    data: Record<string, unknown>[],
    search: string,
    labelsMap: UsageCostLabelsMap
) => {
    const possibleKeys = Object.keys(labelsMap).filter((key) =>
        key.includes(search)
    )

    /**
     * includes only keys that have at least one non-null/undefined value
     * for mixed datasets (if one key is undefined/null and another has a real value)
     * it will be visible on chart & legend
     */
    const lineChartKeys = possibleKeys.filter((key) =>
        data.some((item) => {
            const label = labelsMap[key as keyof typeof labelsMap]
            const value = item[label]
            return value !== null && value !== undefined
        })
    )

    return data.map((item) => {
        const dataItem: Record<string, unknown> = { date: item.date }
        lineChartKeys.forEach((key) => {
            const label = labelsMap[key as keyof typeof labelsMap]
            const value = item[label]
            if (value !== undefined && value !== null) {
                dataItem[label] = value
            }
        })
        return dataItem
    })
}
